import React, { useEffect, useState, useMemo } from 'react';
import { Row, Col, Card, CardBody, Input } from 'reactstrap';
import { activateAuthLayout } from '../../../store/actions';
import { connect } from 'react-redux';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';
import SweetAlert from 'react-bootstrap-sweetalert';
import { useHistory } from 'react-router';
import QuoteSettingsPanel from '../../../components/QuoteSettingsPanel';
import Select from 'react-select';

const GET_PARTNERS = gql`
  query Partners {
    Partners {
      PartnerId
      Name
    }
  }
`;

const QUOTE_TYPES = gql`
  query QuoteTypes {
    QuoteTypes {
      QuoteTypeId
      Name
      Active
    }
  }
`;

const TEMPLATE = gql`
  query getQuote($QuoteId: ID) {
    Quote(QuoteId: $QuoteId) {
      QuoteId
      ProposalTitle
      Description
      PartnerId
      Partner {
        Name
      }
      QuoteTypeId1
      QuoteTypeId2
      QuoteContractMonths1
      QuoteContractMonths2
      GroupItemsByCategory
    }
  }
`;

const CREATE_QUOTE_TEMPLATE = gql`
  mutation createQuoteTemplate($input: CreateQuoteTemplateInput) {
    createQuoteTemplate(input: $input) {
      status
      message
      quote {
        QuoteId
      }
    }
  }
`;

const UPDATE_QUOTE = gql`
  mutation updateQuote($QuoteInput: QuoteInput) {
    updateQuote(input: $QuoteInput) {
      QuoteId
      WorkflowStep
    }
  }
`;

const REFRESH_TEMPLATE_PRICING = gql`
  mutation refreshTemplatePricing($QuoteId: ID!) {
    refreshTemplatePricing(QuoteId: $QuoteId) {
      status
      message
    }
  }
`;

const QuoteTemplateSetup = ({ activateAuthLayout, ...props }) => {
  useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

  const history = useHistory();
  const isNew = !props.quoteId;

  const [proposalTitle, setProposalTitle] = useState('');
  const [description, setDescription] = useState('');
  const [partner, setPartner] = useState(null);
  const [selectedQuoteType1, setSelectedQuoteType1] = useState(1);
  const [selectedQuoteType2, setSelectedQuoteType2] = useState(2);
  const [contractMonths1, setContractMonths1] = useState(null);
  const [contractMonths2, setContractMonths2] = useState(null);
  const [quoteTypes, setQuoteTypes] = useState(null);
  const [groupItemsByCategory, setGroupItemsByCategory] = useState(false);
  const [stepError, setStepError] = useState(null);

  const { data: partnersData } = useQuery(GET_PARTNERS);
  const { data: quoteTypesData } = useQuery(QUOTE_TYPES);
  const { data: templateData } = useQuery(TEMPLATE, {
    variables: { QuoteId: props.quoteId },
    skip: isNew,
  });

  const [createQuoteTemplate] = useMutation(CREATE_QUOTE_TEMPLATE);
  const [updateQuote] = useMutation(UPDATE_QUOTE);
  const [refreshTemplatePricing] = useMutation(REFRESH_TEMPLATE_PRICING);

  const partnerOptions = useMemo(() => (partnersData?.Partners ?? []).map(p => ({ value: p.PartnerId, label: p.Name })), [partnersData]);

  useEffect(() => {
    if (quoteTypesData) {
      setQuoteTypes(Array.from(quoteTypesData.QuoteTypes).slice(1));
    }
  }, [quoteTypesData]);

  useEffect(() => {
    if (templateData && templateData.Quote) {
      const q = templateData.Quote;
      setProposalTitle(q.ProposalTitle || '');
      setDescription(q.Description || '');
      setPartner(q.PartnerId ? { value: q.PartnerId, label: q.Partner.Name } : null);
      setSelectedQuoteType1(q.QuoteTypeId1 != null ? q.QuoteTypeId1 : '');
      setSelectedQuoteType2(q.QuoteTypeId2 != null ? q.QuoteTypeId2 : '');
      setContractMonths1(q.QuoteContractMonths1 ?? null);
      setContractMonths2(q.QuoteContractMonths2 ?? null);
      setGroupItemsByCategory(!!q.GroupItemsByCategory);
      refreshTemplatePricing({ variables: { QuoteId: props.quoteId } });
    }
  }, [templateData, props.quoteId, refreshTemplatePricing]);

  const proceedNextStep = () => {
    if (!proposalTitle.trim()) {
      setStepError('Template name is required');
      return;
    }
    if (selectedQuoteType1 && contractMonths1 == null) {
      setStepError('Contract period is required for Quote 1');
      return;
    }
    if (selectedQuoteType2 && contractMonths2 == null) {
      setStepError('Contract period is required for Quote 2');
      return;
    }

    const fields = {
      ProposalTitle: proposalTitle,
      Description: description || null,
      PartnerId: partner ? parseInt(partner.value) : null,
      QuoteTypeId1: selectedQuoteType1 !== '' ? parseInt(selectedQuoteType1, 10) : null,
      QuoteTypeId2: selectedQuoteType2 !== '' ? parseInt(selectedQuoteType2, 10) : null,
      QuoteContractMonths1: parseInt(contractMonths1, 10),
      QuoteContractMonths2: parseInt(contractMonths2, 10),
      GroupItemsByCategory: groupItemsByCategory,
    };

    if (isNew) {
      createQuoteTemplate({ variables: { input: fields } }).then(({ data }) => {
        const quoteId = data.createQuoteTemplate.quote.QuoteId;
        history.replace(`/quote-items/${quoteId}`);
      });
    } else {
      updateQuote({
        variables: {
          QuoteInput: {
            QuoteId: parseInt(props.quoteId, 10),
            ...fields,
            WorkflowStep: 'QUOTE_ITEMS',
          },
        },
      }).then(() => {
        history.replace(`/quote-items/${props.quoteId}`);
      });
    }
  };

  const changeQuoteType1 = setSelectedQuoteType1;
  const changeQuoteType2 = setSelectedQuoteType2;

  return (
    <React.Fragment>
      {stepError && (
        <SweetAlert
          error
          title="Error"
          onConfirm={() => setStepError(null)}
        >
          {stepError}
        </SweetAlert>
      )}

      {(isNew || templateData?.Quote) && (
        <div className="content">
          <div className="container-fluid">
            <div className="page-title-box">
              <Row className="align-items-center">
                <Col sm="6">
                  <h4 className="page-title">
                    {isNew ? 'New Template' : `${proposalTitle} - Template Setup`}
                  </h4>
                </Col>
              </Row>
            </div>

            <Row>
              <Col lg="12">
                <Card>
                  <CardBody>
                    <h4 className="mt-0 header-title">Template Details</h4>
                    <table className="table">
                      <tbody>
                        <tr>
                          <th scope="row">Template name <span className="text-danger">*</span></th>
                          <td>
                            <Input
                              type="text"
                              value={proposalTitle}
                              onChange={(e) => setProposalTitle(e.target.value)}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">Template description</th>
                          <td>
                            <Input
                              type="text"
                              value={description}
                              onChange={(e) => setDescription(e.target.value)}
                            />
                          </td>
                        </tr>
                        {partnerOptions.length > 0 && (
                        <tr>
                          <th scope="row">Partner</th>
                          <td>
                            <Select
                              isClearable
                              options={partnerOptions}
                              value={partner}
                              placeholder="None"
                              onChange={setPartner}
                            />
                          </td>
                        </tr>
                        )}
                        <tr>
                          <th scope="row"></th>
                          <td>
                            <div className="d-flex align-items-center">
                              <input
                                type="checkbox"
                                id="group-items-by-category"
                                checked={groupItemsByCategory}
                                onChange={(e) => setGroupItemsByCategory(e.target.checked)}
                              />
                              <label htmlFor="group-items-by-category" className="ml-2 mb-0">
                                Group quote items by category on proposal pricing tables
                              </label>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <QuoteSettingsPanel
              title="Quote 1 Settings"
              quoteTypes={quoteTypes}
              quoteTypeValue={selectedQuoteType1}
              contractMonths={contractMonths1}
              onQuoteTypeChange={changeQuoteType1}
              onContractMonthsChange={setContractMonths1}
              otherQuoteTypeValue={selectedQuoteType2}
            />

            <QuoteSettingsPanel
              title="Quote 2 Settings"
              quoteTypes={quoteTypes}
              quoteTypeValue={selectedQuoteType2}
              contractMonths={contractMonths2}
              onQuoteTypeChange={changeQuoteType2}
              onContractMonthsChange={setContractMonths2}
              otherQuoteTypeValue={selectedQuoteType1}
            />

            <Row>
              <Col lg="12">
                <Card align="right">
                  <CardBody>
                    <button
                      className="btn-icon btn btn-primary btn-lg"
                      onClick={proceedNextStep}
                    >
                      {' '}
                      <span className="btn-icon-label">
                        <i className="ion ion-md-arrow-forward mr-2"></i>
                      </span>{' '}
                      Next Step
                    </button>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default connect(null, { activateAuthLayout })(QuoteTemplateSetup);
